import React, { useState, useEffect, useRef } from "react";
import { Send, Loader2, Users, Award, CheckCircle } from "lucide-react";

const API_CONFIG = {
  FILTER_API: "https://6hvrdenyomumgqs3c5retvfnyy0rtdns.lambda-url.us-east-1.on.aws"
};

const ResumeFilteringAgent = ({ selectedRole, roles, onRoleSelect }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const pushMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const updateMessageText = (id, newText, isThinking = false, topResumes = null, metrics = null) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, text: newText, isThinking, topResumes, metrics } : m
      )
    );
  };

  const extractTopResumes = (text) => {
    try {
      console.log("=== EXTRACTING RESUMES FROM TEXT ===");
      console.log("Text length:", text.length);
      console.log("=== FULL RESPONSE TEXT ===");
      console.log(text);
      console.log("=== END FULL RESPONSE TEXT ===");
      
      // First, try to parse if the entire response is JSON
      try {
        const jsonData = JSON.parse(text);
        if (Array.isArray(jsonData)) {
          console.log("Entire response is JSON array:", jsonData);
          const resumes = jsonData.map((r, idx) => ({
            rank: r.rank || r.Rank || idx + 1,
            name: r.name || r.Name || r.candidate || r.Candidate || r.resume_name || r.id || r.ID || `Candidate ${idx + 1}`,
            score: parseFloat(r.score || r.Score || r.rating || r.Rating || 0)
          }));
          // Sort by score HIGH to LOW
          resumes.sort((a, b) => b.score - a.score);
          resumes.forEach((resume, idx) => { resume.rank = idx + 1; });
          return resumes;
        } else if (jsonData.candidates || jsonData.resumes || jsonData.results) {
          const arr = jsonData.candidates || jsonData.resumes || jsonData.results;
          console.log("Found nested array:", arr);
          const resumes = arr.map((r, idx) => ({
            rank: r.rank || r.Rank || idx + 1,
            name: r.name || r.Name || r.candidate || r.Candidate || r.resume_name || r.id || r.ID || `Candidate ${idx + 1}`,
            score: parseFloat(r.score || r.Score || r.rating || r.Rating || 0)
          }));
          // Sort by score HIGH to LOW
          console.log("=== BEFORE SORTING (JSON) ===", resumes);
          resumes.sort((a, b) => b.score - a.score);
          console.log("=== AFTER SORTING (JSON) ===", resumes);
          resumes.forEach((resume, idx) => { resume.rank = idx + 1; });
          return resumes;
        }
      } catch (e) {
        console.log("Not a JSON response, trying text parsing");
      }
      
      // Try to find JSON array anywhere in the text
      const jsonMatch = text.match(/\[\s*\{[\s\S]*?\}\s*\]/);
      if (jsonMatch) {
        try {
          const resumes = JSON.parse(jsonMatch[0]);
          if (Array.isArray(resumes) && resumes.length > 0) {
            console.log("Found JSON array in text:", resumes);
            return resumes.map((r, idx) => ({
              rank: r.rank || r.Rank || idx + 1,
              name: r.name || r.Name || r.candidate || r.Candidate || r.resume_name || r.id || r.ID || `Candidate ${idx + 1}`,
              score: parseFloat(r.score || r.Score || r.rating || r.Rating || 0)
            }));
          }
        } catch (e) {
          console.log("JSON parse failed, continuing with text extraction");
        }
      }

      // Text-based extraction with comprehensive patterns
      const lines = text.split('\n');
      const resumes = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || line.length < 5) continue;
        
        console.log(`Checking line ${i}: "${line}"`);
        
        // Comprehensive patterns for resume names
        const patterns = [
          // "DineshKagitapu_AWSCloudEngineer_Resume - Score: 9"
          /^([A-Za-z0-9_\-\s\.]+?)\s*[-–—:]\s*Score:\s*(\d+(?:\.\d+)?)/i,
          // "1. **Resume_Name** - Score: 8.5/10" or "1. **Resume_Name** - 8.5/10"
          /^(\d+)\.\s*\*\*([^*]+?)\*\*\s*[-–—:]\s*(?:Score|Rating|Match)?:?\s*(\d+(?:\.\d+)?)\s*(?:\/\s*10)?/i,
          // "1. Resume_Name - Score: 8.5" or "1. Resume_Name - 8.5"
          /^(\d+)[.\)]\s*([A-Za-z0-9_\-\s\.]+?)\s*[-–—:]\s*(?:Score|Rating|Match)?:?\s*(\d+(?:\.\d+)?)\s*(?:\/\s*10)?/i,
          // "Rank 1: Resume_Name - 8.5" or "#1: Resume_Name - 8.5"
          /^(?:Rank|#)\s*(\d+)\s*[:.\)]\s*([A-Za-z0-9_\-\s\.]+?)\s*[-–—:]\s*(\d+(?:\.\d+)?)\s*(?:\/\s*10)?/i,
          // "Resume_Name: 8.5/10" or "Resume_Name: 8.5"
          /^([A-Za-z0-9_\-\s\.]{3,50}?)\s*[:]\s*(\d+(?:\.\d+)?)\s*(?:\/\s*10)?$/i,
          // "**Resume_Name**: 8.5/10"
          /^\*\*([A-Za-z0-9_\-\s\.]+?)\*\*\s*[:]\s*(\d+(?:\.\d+)?)\s*(?:\/\s*10)?/i,
          // "1) Resume_Name (8.5/10)"
          /^(\d+)\)\s*([A-Za-z0-9_\-\s\.]+?)\s*\((\d+(?:\.\d+)?)\s*(?:\/\s*10)?\)/i,
          // "Candidate 1: Resume_Name - 8.5"
          /^(?:Candidate|Resume)\s*(\d+)\s*[:.\)]\s*([A-Za-z0-9_\-\s\.]+?)\s*[-–—:]\s*(\d+(?:\.\d+)?)/i,
        ];
        
        for (let p = 0; p < patterns.length; p++) {
          const pattern = patterns[p];
          const match = line.match(pattern);
          
          if (match) {
            console.log(`✓ Pattern ${p} matched! Groups:`, match);
            
            let rank, name, score;
            
            // Pattern 0: Direct "Resume_Name - Score: X" format
            if (p === 0 && match.length === 3) {
              name = match[1].trim();
              score = parseFloat(match[2]);
              rank = resumes.length + 1;
            }
            // Patterns with rank number (1, 2, 6, 7)
            else if (match.length === 4 && (p === 1 || p === 2 || p === 6 || p === 7)) {
              rank = parseInt(match[1]);
              name = match[2].trim();
              score = parseFloat(match[3]);
            }
            // Patterns with rank number (3)
            else if (p === 3 && match.length === 4) {
              rank = parseInt(match[1]);
              name = match[2].trim();
              score = parseFloat(match[3]);
            }
            // Patterns without rank (name comes first) (4, 5)
            else if ((p === 4 || p === 5) && match.length === 3) {
              name = match[1].trim();
              score = parseFloat(match[2]);
              rank = resumes.length + 1;
            }
            else {
              continue;
            }
            
            // Clean up name
            name = name.replace(/[:\-–—]$/, '').trim();
            name = name.replace(/\s+/g, ' '); // Remove extra spaces
            
            // Validate - accept resume filename formats
            if (name && name.length >= 3 && name.length < 100 && !isNaN(score) && score >= 0 && score <= 10) {
              // Check for duplicates
              if (!resumes.some(r => r.name.toLowerCase() === name.toLowerCase())) {
                resumes.push({ rank, name, score });
                console.log(`✓✓ Added: Rank ${rank}, Name: "${name}", Score: ${score}`);
              } else {
                console.log(`× Duplicate name: "${name}"`);
              }
              break;
            } else {
              console.log(`× Validation failed: name="${name}", score=${score}, nameLength=${name.length}`);
            }
          }
        }
      }

      // Sort by score (highest to lowest)
      console.log("=== BEFORE SORTING ===", resumes);
      resumes.sort((a, b) => {
        console.log(`Comparing ${a.name}(${a.score}) vs ${b.name}(${b.score}): ${b.score - a.score}`);
        return b.score - a.score;
      });
      console.log("=== AFTER SORTING ===", resumes);
      
      // Re-assign ranks based on sorted order (1 = highest score)
      resumes.forEach((resume, idx) => {
        resume.rank = idx + 1;
      });
      
      console.log("=== FINAL EXTRACTED RESUMES (sorted HIGH to LOW) ===", resumes);
      console.log("Total extracted:", resumes.length);
      
      return resumes.length > 0 ? resumes : null;
    } catch (err) {
      console.error("Error extracting resumes:", err);
      return null;
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;
    if (!selectedRole) return alert("Please select a role first.");

    const text = inputValue;
    setInputValue("");

    const userId = `u-${Date.now()}`;
    pushMessage({ id: userId, text, sender: "user" });

    const thinkingId = `b-${Date.now()}`;
    pushMessage({
      id: thinkingId,
      text: "Analyzing resumes...",
      sender: "bot",
      isThinking: true,
    });

    setIsLoading(true);

    try {
      console.log("=== SENDING REQUEST ===");
      console.log("URL:", API_CONFIG.FILTER_API);
      console.log("Body:", { question: text, role: selectedRole });

      const response = await fetch(API_CONFIG.FILTER_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: text, role: selectedRole }),
      });

      console.log("=== RESPONSE STATUS ===", response.status);

      const data = await response.json();
      console.log("=== RAW DATA ===", data);
      
      // Parse the Lambda proxy response
      let final = "";
      let responseMetrics = null;
      
      if (data.body) {
        console.log("Found data.body, parsing...");
        const bodyData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        console.log("=== BODY DATA ===", bodyData);
        console.log("=== BODY DATA.RESULT ===", bodyData.result);
        
        // Extract metrics if available
        if (bodyData.metrics) {
          responseMetrics = bodyData.metrics;
          console.log("=== EXTRACTED METRICS FROM OBJECT ===", responseMetrics);
        }
        
        // Check multiple possible locations for response text
        if (bodyData.response) {
          // New format with 'response' field (our updated Lambda)
          final = bodyData.response;
          console.log("Extracted from bodyData.response (new format)");
        } else if (bodyData.result?.content?.[0]?.text) {
          final = bodyData.result.content[0].text;
          console.log("Extracted from bodyData.result.content[0].text");
        } else if (bodyData.result?.result?.content?.[0]?.text) {
          final = bodyData.result.result.content[0].text;
          console.log("Extracted from bodyData.result.result.content[0].text");
        } else if (typeof bodyData.result === 'string') {
          final = bodyData.result;
          console.log("Extracted from bodyData.result as string");
        } else if (bodyData.result) {
          // Result exists but in unknown format, stringify it
          console.log("Result exists but format unknown, stringifying");
          final = JSON.stringify(bodyData.result, null, 2);
        }
      }
      
      if (!final) {
        console.log("Trying fallback extraction...");
        final = data.content?.[0]?.text || data.reply || data.message || data.result || "No response received.";
      }

      console.log("=== FINAL TEXT (before cleanup) ===", final);

      // Clean up the text - remove escaped characters and code blocks
      if (typeof final === 'string') {
        final = final.replace(/\\n/g, "\n");
        final = final.replace(/\\"/g, '"');
        final = final.replace(/```json\n?|\n?```/g, '').trim();
      } else if (typeof final === 'object') {
        final = JSON.stringify(final, null, 2);
      }

      console.log("=== FINAL TEXT (after cleanup) ===", final);
      console.log("=== FINAL TEXT TYPE ===", typeof final);
      console.log("=== FINAL TEXT LENGTH ===", final.length);
      console.log("=== CHECKING FOR TOKEN USAGE IN TEXT ===");
      console.log("Text contains 'Input:':", final.includes('Input:'));
      console.log("Text contains 'tokens':", final.includes('tokens'));
      console.log("Text contains 'ms':", final.includes('ms'));

      // Extract token usage from the response text if not already in metrics
      if (!responseMetrics && typeof final === 'string') {
        console.log("=== ATTEMPTING TOKEN EXTRACTION ===");
        // Updated regex to match the exact format: "Input: 42,451 tokens\nOutput: 1,305 tokens\nTotal: 43,756 tokens\n1532.50ms"
        const tokenMatch = final.match(/Input:\s*([\d,]+)\s*tokens\s*\n*Output:\s*([\d,]+)\s*tokens\s*\n*Total:\s*([\d,]+)\s*tokens\s*\n*([\d.]+)ms/i);
        console.log("Token regex match result:", tokenMatch);
        
        if (tokenMatch) {
          responseMetrics = {
            inputTokens: parseInt(tokenMatch[1].replace(/,/g, '')),
            outputTokens: parseInt(tokenMatch[2].replace(/,/g, '')),
            totalTokens: parseInt(tokenMatch[3].replace(/,/g, '')),
            executionTimeMs: parseFloat(tokenMatch[4])
          };
          console.log("=== EXTRACTED METRICS FROM TEXT ===", responseMetrics);
          
          // Remove token usage from display text (it will show in footer)
          final = final.replace(/\s*Input:\s*[\d,]+\s*tokens\s*\n*Output:\s*[\d,]+\s*tokens\s*\n*Total:\s*[\d,]+\s*tokens\s*\n*[\d.]+ms\s*/gi, '');
          final = final.trim();
          console.log("=== TEXT AFTER TOKEN REMOVAL ===", final.slice(-100));
        } else {
          console.log("=== NO TOKEN MATCH FOUND ===");
          // Show the last 200 characters to see what's at the end
          console.log("=== LAST 200 CHARS OF RESPONSE ===");
          console.log(final.slice(-200));
        }
      } else {
        console.log("=== METRICS ALREADY AVAILABLE OR TEXT NOT STRING ===");
        console.log("responseMetrics:", responseMetrics);
      }

      // Extract top resumes from the response
      const topResumes = extractTopResumes(final);
      console.log("=== EXTRACTED TOP RESUMES ===", topResumes);
      
      // If extraction failed, try to log the text structure
      if (!topResumes || topResumes.length === 0) {
        console.log("=== EXTRACTION FAILED - FIRST 500 CHARS ===");
        console.log(final.substring(0, 500));
        console.log("=== SHOWING ALL LINES ===");
        final.split('\n').forEach((line, idx) => {
          if (line.trim()) console.log(`Line ${idx}: "${line}"`);
        });
      }

      updateMessageText(thinkingId, final, false, topResumes, responseMetrics);
    } catch (err) {
      console.error("=== ERROR ===", err);
      updateMessageText(
        thinkingId,
        `⚠️ Error: ${err.message}`,
        false
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 relative">
      {/* CHAT AREA */}
      <div className="flex-1 overflow-y-auto p-6">
        {messages.length === 0 ? (
          // BEAUTIFUL EMPTY STATE
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-xl opacity-90">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <Users className="w-12 h-12 text-orange-600" />
              </div>

              <h2 className="text-4xl font-semibold text-gray-900 mb-3">
                Recruiter Agent
              </h2>
              <p className="text-gray-600 text-lg">
                Select a role and ask a hiring question to begin.
              </p>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto w-full space-y-6">
            {messages.map((msg) => {
              const isUser = msg.sender === "user";

              return (
                <div
                  key={msg.id}
                  className={`flex items-start gap-4 ${
                    isUser ? "justify-end" : "justify-start"
                  }`}
                >
                  {/* Avatar */}
                  {!isUser && (
                    <div className="w-11 h-11 rounded-full bg-orange-600 flex items-center justify-center shadow-md">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  )}

                  {/* BUBBLE */}
                  <div
                    className={`max-w-2xl px-6 py-5 shadow-md text-[15px] leading-[1.7] font-['system-ui',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif]
                      ${
                        isUser
                          ? "bg-white text-gray-900 rounded-2xl rounded-br-md border border-gray-200"
                          : "bg-white text-gray-800 rounded-2xl rounded-bl-md border border-gray-200"
                      }`}
                  >
                    {msg.isThinking ? (
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                        <span className="text-gray-700 font-medium">Analyzing resumes...</span>
                      </div>
                    ) : (
                      <>
                        <div className={`whitespace-pre-wrap ${isUser ? 'text-gray-900' : 'text-gray-800'}`}>
                          {/* Render text with markdown-style bold formatting */}
                          {msg.text.split(/(\*\*[^*]+\*\*)/).map((part, index) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={index} className="text-lg font-bold">{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </div>
                        
                        {/* Top Resumes List */}
                        {msg.topResumes && msg.topResumes.length > 0 && (
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="flex items-center gap-2 mb-4">
                              <Award className="w-5 h-5 text-orange-600" />
                              <h3 className="font-semibold text-gray-900 text-base">Top Candidates</h3>
                            </div>
                            <div className="space-y-2.5">
                              {msg.topResumes.map((resume, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all group cursor-pointer"
                                >
                                  <div className="flex items-center gap-3 flex-1">
                                    <div className="w-9 h-9 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                      {resume.rank}
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-semibold text-gray-900 group-hover:text-orange-600 transition text-[15px]">
                                        {resume.name}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-2xl font-bold text-orange-600">
                                        {resume.score}
                                      </span>
                                      <span className="text-gray-500 text-xs font-medium">/10</span>
                                    </div>
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Simple Token Footer */}
                        {msg.metrics && !isUser && (
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <div className="flex items-center gap-4">
                                <span>Input: {msg.metrics.inputTokens?.toLocaleString() || 0} tokens</span>
                                <span>Output: {msg.metrics.outputTokens?.toLocaleString() || 0} tokens</span>
                                <span>Total: {msg.metrics.totalTokens?.toLocaleString() || 0} tokens</span>
                              </div>
                              <span>
                                {msg.metrics.executionTimeMs ? `${msg.metrics.executionTimeMs}ms` : 
                                 msg.metrics.latencyMs ? `${msg.metrics.latencyMs}ms` : 'N/A'}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* USER AVATAR */}
                  {isUser && (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">U</span>
                    </div>
                  )}
                </div>
              );
            })}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* FLOATING INPUT BAR */}
      <div className="w-full flex justify-center py-6">
        <div className="w-[92%] max-w-4xl bg-white/80 backdrop-blur-xl border border-gray-200 rounded-full px-6 py-3 shadow-lg flex items-center gap-3">
          {/* Input */}
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendMessage();
            }}
            placeholder={
              selectedRole
                ? "Ask something about the resumes..."
                : "Select a role to start..."
            }
            className="flex-1 bg-transparent outline-none text-gray-800 text-base"
          />

          {/* ROLE DROPDOWN */}
          <select
            value={selectedRole || ""}
            onChange={(e) => onRoleSelect(e.target.value)}
            className="bg-white border border-gray-300 rounded-full px-4 py-2 text-gray-700 text-sm shadow-sm hover:shadow focus:ring-2 focus:ring-orange-500"
          >
            <option value="">Role</option>
            {roles?.map((role) => (
              <option key={role} value={role}>
                {role.slice(0, -1)}
              </option>
            ))}
          </select>

          {/* Send Button */}
          <button
            onClick={sendMessage}
            disabled={!selectedRole || !inputValue.trim() || isLoading}
            className={`w-11 h-11 flex items-center justify-center rounded-full transition
              ${
                !selectedRole || !inputValue.trim() || isLoading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700 text-white shadow-md"
              }`}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeFilteringAgent;